import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const value = {
    language,
    setLanguage,
    t: (key) => translations[language][key] || key
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Translations object
const translations = {
  en: {
    // Navigation
    dashboard: 'Dashboard',
    bloodSugar: 'Blood Sugar',
    meals: 'Meals',
    medications: 'Medications',
    exercise: 'Exercise',
    healthTracking: 'Health Tracking',
    analytics: 'Analytics',
    aiInsights: 'AI Insights',
    smartFeatures: 'Smart Features',
    foodScanner: 'Food Scanner',
    recipes: 'Recipes',
    shop: 'Shop',
    community: 'Community',
    careTeam: 'Care Team',
    reports: 'Reports',
    pricing: 'Pricing',
    profile: 'Profile',
    admin: 'Admin',
    logout: 'Logout',
    
    // Common
    search: 'Search',
    filter: 'Filter',
    add: 'Add',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    loading: 'Loading',
    error: 'Error',
    success: 'Success',
    next: 'Next',
    previous: 'Previous',
    
    // Admin
    adminLogin: 'Admin Login',
    username: 'Username',
    password: 'Password',
    login: 'Login',
    amazonProductsManagement: 'Amazon Products Management',
    recipeManagement: 'Recipe Management',
    addManageProducts: 'Add and manage shop products',
    addManageRecipes: 'Add and manage diabetes-friendly recipes',
    newProduct: 'New Product',
    newRecipe: 'New Recipe',
    
    // Recipes
    searchRecipes: 'Search recipes...',
    allMeals: 'All meals',
    breakfast: 'Breakfast',
    lunch: 'Lunch',
    dinner: 'Dinner',
    snack: 'Snack',
    smoothie: 'Smoothie',
    recipesFound: 'recipes found',
    noRecipesFound: 'No recipes found',
    loadingRecipes: 'Loading recipes...',
    
    // Food Scanner
    foodScannerTitle: 'Food Scanner',
    analyzeCarbs: 'Analyze carbs instantly',
    scanBarcode: 'Scan a Barcode',
    searchFood: 'Search for Food',
    carbsAnalysis: 'Carbs Analysis',
    totalCarbs: 'Total Carbs',
    sugars: 'Sugars',
    calories: 'Calories',
    protein: 'Protein',
    fats: 'Fats',
    fiber: 'Fiber',
    ingredients: 'Ingredients',
    productNotFound: 'Product not found',
    
    // Messages
    productAddedSuccess: 'Product added successfully',
    productDeletedSuccess: 'Product deleted successfully',
    recipeAddedSuccess: 'Recipe added successfully',
    recipeDeletedSuccess: 'Recipe deleted successfully',
    errorLoading: 'Error loading',
    errorSaving: 'Error saving',
    
    // Misc
    openScanner: 'Open Scanner',
    viewDetails: 'View details',
    carbs: 'carbs',
  },
  
  fr: {
    // Navigation
    dashboard: 'Tableau de bord',
    bloodSugar: 'Glycémie',
    meals: 'Repas',
    medications: 'Médicaments',
    exercise: 'Exercice',
    healthTracking: 'Suivi Santé',
    analytics: 'Analyses',
    aiInsights: 'Analyses IA',
    smartFeatures: 'Fonctions Intelligentes',
    foodScanner: 'Scanner Alimentaire',
    recipes: 'Recettes',
    shop: 'Boutique',
    community: 'Communauté',
    careTeam: 'Équipe Soignante',
    reports: 'Rapports',
    pricing: 'Tarifs',
    profile: 'Profil',
    admin: 'Admin',
    logout: 'Déconnexion',
    
    // Common
    search: 'Rechercher',
    filter: 'Filtrer',
    add: 'Ajouter',
    edit: 'Modifier',
    delete: 'Supprimer',
    save: 'Enregistrer',
    cancel: 'Annuler',
    loading: 'Chargement',
    error: 'Erreur',
    success: 'Succès',
    next: 'Suivant',
    previous: 'Précédent',
    
    // Admin
    adminLogin: 'Connexion Admin',
    username: "Nom d'utilisateur",
    password: 'Mot de passe',
    login: 'Se connecter',
    amazonProductsManagement: 'Gestion Produits Amazon',
    recipeManagement: 'Gestion Recettes',
    addManageProducts: 'Ajoutez et gérez les produits',
    addManageRecipes: 'Ajoutez et gérez les recettes',
    newProduct: 'Nouveau Produit',
    newRecipe: 'Nouvelle Recette',
    
    // Recipes
    searchRecipes: 'Rechercher une recette...',
    allMeals: 'Tous les repas',
    breakfast: 'Petit-déjeuner',
    lunch: 'Déjeuner',
    dinner: 'Dîner',
    snack: 'Collation',
    smoothie: 'Smoothie',
    recipesFound: 'recettes trouvées',
    noRecipesFound: 'Aucune recette trouvée',
    loadingRecipes: 'Chargement des recettes...',
    
    // Food Scanner
    foodScannerTitle: 'Scanner Alimentaire',
    analyzeCarbs: 'Analysez les glucides instantanément',
    scanBarcode: 'Scanner un code-barres',
    searchFood: 'Rechercher un aliment',
    carbsAnalysis: 'Analyse des Glucides',
    totalCarbs: 'Glucides Totaux',
    sugars: 'Sucres',
    calories: 'Calories',
    protein: 'Protéines',
    fats: 'Lipides',
    fiber: 'Fibres',
    ingredients: 'Ingrédients',
    productNotFound: 'Produit non trouvé',
    
    // Messages
    productAddedSuccess: 'Produit ajouté avec succès',
    productDeletedSuccess: 'Produit supprimé avec succès',
    recipeAddedSuccess: 'Recette ajoutée avec succès',
    recipeDeletedSuccess: 'Recette supprimée avec succès',
    errorLoading: 'Erreur lors du chargement',
    errorSaving: 'Erreur lors de la sauvegarde',
    
    // Misc
    openScanner: 'Ouvrir le Scanner',
    viewDetails: 'Voir détails',
    carbs: 'glucides',
  },
  
  es: {
    // Navigation
    dashboard: 'Panel',
    bloodSugar: 'Glucosa',
    meals: 'Comidas',
    medications: 'Medicamentos',
    exercise: 'Ejercicio',
    healthTracking: 'Seguimiento de Salud',
    analytics: 'Análisis',
    aiInsights: 'Análisis IA',
    smartFeatures: 'Funciones Inteligentes',
    foodScanner: 'Escáner de Alimentos',
    recipes: 'Recetas',
    shop: 'Tienda',
    community: 'Comunidad',
    careTeam: 'Equipo Médico',
    reports: 'Informes',
    pricing: 'Precios',
    profile: 'Perfil',
    admin: 'Admin',
    logout: 'Cerrar sesión',
    
    // Common
    search: 'Buscar',
    filter: 'Filtrar',
    add: 'Agregar',
    edit: 'Editar',
    delete: 'Eliminar',
    save: 'Guardar',
    cancel: 'Cancelar',
    loading: 'Cargando',
    error: 'Error',
    success: 'Éxito',
    next: 'Siguiente',
    previous: 'Anterior',
    
    // Admin
    adminLogin: 'Inicio Admin',
    username: 'Usuario',
    password: 'Contraseña',
    login: 'Iniciar sesión',
    amazonProductsManagement: 'Gestión Productos Amazon',
    recipeManagement: 'Gestión Recetas',
    addManageProducts: 'Agregar y gestionar productos',
    addManageRecipes: 'Agregar y gestionar recetas',
    newProduct: 'Nuevo Producto',
    newRecipe: 'Nueva Receta',
    
    // Recipes
    searchRecipes: 'Buscar recetas...',
    allMeals: 'Todas las comidas',
    breakfast: 'Desayuno',
    lunch: 'Almuerzo',
    dinner: 'Cena',
    snack: 'Merienda',
    smoothie: 'Batido',
    recipesFound: 'recetas encontradas',
    noRecipesFound: 'No se encontraron recetas',
    loadingRecipes: 'Cargando recetas...',
    
    // Food Scanner
    foodScannerTitle: 'Escáner de Alimentos',
    analyzeCarbs: 'Analizar carbohidratos al instante',
    scanBarcode: 'Escanear código de barras',
    searchFood: 'Buscar alimento',
    carbsAnalysis: 'Análisis de Carbohidratos',
    totalCarbs: 'Carbohidratos Totales',
    sugars: 'Azúcares',
    calories: 'Calorías',
    protein: 'Proteína',
    fats: 'Grasas',
    fiber: 'Fibra',
    ingredients: 'Ingredientes',
    productNotFound: 'Producto no encontrado',
    
    // Messages
    productAddedSuccess: 'Producto agregado exitosamente',
    productDeletedSuccess: 'Producto eliminado exitosamente',
    recipeAddedSuccess: 'Receta agregada exitosamente',
    recipeDeletedSuccess: 'Receta eliminada exitosamente',
    errorLoading: 'Error al cargar',
    errorSaving: 'Error al guardar',
    
    // Misc
    openScanner: 'Abrir Escáner',
    viewDetails: 'Ver detalles',
    carbs: 'carbohidratos',
  },
  
  de: {
    // Navigation
    dashboard: 'Dashboard',
    bloodSugar: 'Blutzucker',
    meals: 'Mahlzeiten',
    medications: 'Medikamente',
    exercise: 'Übung',
    healthTracking: 'Gesundheits-Tracking',
    analytics: 'Analytik',
    aiInsights: 'KI-Einblicke',
    smartFeatures: 'Intelligente Funktionen',
    foodScanner: 'Lebensmittel-Scanner',
    recipes: 'Rezepte',
    shop: 'Shop',
    community: 'Gemeinschaft',
    careTeam: 'Pflegeteam',
    reports: 'Berichte',
    pricing: 'Preise',
    profile: 'Profil',
    admin: 'Admin',
    logout: 'Abmelden',
    
    // Common
    search: 'Suchen',
    filter: 'Filter',
    add: 'Hinzufügen',
    edit: 'Bearbeiten',
    delete: 'Löschen',
    save: 'Speichern',
    cancel: 'Abbrechen',
    loading: 'Laden',
    error: 'Fehler',
    success: 'Erfolg',
    next: 'Weiter',
    previous: 'Zurück',
    
    // Admin
    adminLogin: 'Admin-Anmeldung',
    username: 'Benutzername',
    password: 'Passwort',
    login: 'Anmelden',
    amazonProductsManagement: 'Amazon Produktverwaltung',
    recipeManagement: 'Rezeptverwaltung',
    addManageProducts: 'Produkte hinzufügen und verwalten',
    addManageRecipes: 'Rezepte hinzufügen und verwalten',
    newProduct: 'Neues Produkt',
    newRecipe: 'Neues Rezept',
    
    // Recipes
    searchRecipes: 'Rezepte suchen...',
    allMeals: 'Alle Mahlzeiten',
    breakfast: 'Frühstück',
    lunch: 'Mittagessen',
    dinner: 'Abendessen',
    snack: 'Snack',
    smoothie: 'Smoothie',
    recipesFound: 'Rezepte gefunden',
    noRecipesFound: 'Keine Rezepte gefunden',
    loadingRecipes: 'Rezepte werden geladen...',
    
    // Food Scanner
    foodScannerTitle: 'Lebensmittel-Scanner',
    analyzeCarbs: 'Kohlenhydrate sofort analysieren',
    scanBarcode: 'Barcode scannen',
    searchFood: 'Lebensmittel suchen',
    carbsAnalysis: 'Kohlenhydratanalyse',
    totalCarbs: 'Gesamt-Kohlenhydrate',
    sugars: 'Zucker',
    calories: 'Kalorien',
    protein: 'Protein',
    fats: 'Fette',
    fiber: 'Ballaststoffe',
    ingredients: 'Zutaten',
    productNotFound: 'Produkt nicht gefunden',
    
    // Messages
    productAddedSuccess: 'Produkt erfolgreich hinzugefügt',
    productDeletedSuccess: 'Produkt erfolgreich gelöscht',
    recipeAddedSuccess: 'Rezept erfolgreich hinzugefügt',
    recipeDeletedSuccess: 'Rezept erfolgreich gelöscht',
    errorLoading: 'Fehler beim Laden',
    errorSaving: 'Fehler beim Speichern',
    
    // Misc
    openScanner: 'Scanner öffnen',
    viewDetails: 'Details anzeigen',
    carbs: 'kohlenhydrate',
  },
  
  zh: {
    // Navigation
    dashboard: '仪表板',
    bloodSugar: '血糖',
    meals: '膳食',
    medications: '药物',
    exercise: '运动',
    healthTracking: '健康跟踪',
    analytics: '分析',
    aiInsights: 'AI洞察',
    smartFeatures: '智能功能',
    foodScanner: '食物扫描仪',
    recipes: '食谱',
    shop: '商店',
    community: '社区',
    careTeam: '护理团队',
    reports: '报告',
    pricing: '定价',
    profile: '个人资料',
    admin: '管理员',
    logout: '登出',
    
    // Common
    search: '搜索',
    filter: '筛选',
    add: '添加',
    edit: '编辑',
    delete: '删除',
    save: '保存',
    cancel: '取消',
    loading: '加载中',
    error: '错误',
    success: '成功',
    next: '下一页',
    previous: '上一页',
    
    // Admin
    adminLogin: '管理员登录',
    username: '用户名',
    password: '密码',
    login: '登录',
    amazonProductsManagement: '亚马逊产品管理',
    recipeManagement: '食谱管理',
    addManageProducts: '添加和管理产品',
    addManageRecipes: '添加和管理食谱',
    newProduct: '新产品',
    newRecipe: '新食谱',
    
    // Recipes
    searchRecipes: '搜索食谱...',
    allMeals: '所有餐食',
    breakfast: '早餐',
    lunch: '午餐',
    dinner: '晚餐',
    snack: '小吃',
    smoothie: '冰沙',
    recipesFound: '找到的食谱',
    noRecipesFound: '未找到食谱',
    loadingRecipes: '加载食谱中...',
    
    // Food Scanner
    foodScannerTitle: '食物扫描仪',
    analyzeCarbs: '即时分析碳水化合物',
    scanBarcode: '扫描条形码',
    searchFood: '搜索食物',
    carbsAnalysis: '碳水化合物分析',
    totalCarbs: '总碳水化合物',
    sugars: '糖',
    calories: '卡路里',
    protein: '蛋白质',
    fats: '脂肪',
    fiber: '纤维',
    ingredients: '成分',
    productNotFound: '未找到产品',
    
    // Messages
    productAddedSuccess: '产品添加成功',
    productDeletedSuccess: '产品删除成功',
    recipeAddedSuccess: '食谱添加成功',
    recipeDeletedSuccess: '食谱删除成功',
    errorLoading: '加载错误',
    errorSaving: '保存错误',
    
    // Misc
    openScanner: '打开扫描仪',
    viewDetails: '查看详情',
    carbs: '碳水化合物',
  },
  
  ja: {
    // Navigation
    dashboard: 'ダッシュボード',
    bloodSugar: '血糖値',
    meals: '食事',
    medications: '薬',
    exercise: '運動',
    healthTracking: '健康追跡',
    analytics: '分析',
    aiInsights: 'AI洞察',
    smartFeatures: 'スマート機能',
    foodScanner: '食品スキャナー',
    recipes: 'レシピ',
    shop: 'ショップ',
    community: 'コミュニティ',
    careTeam: 'ケアチーム',
    reports: 'レポート',
    pricing: '価格',
    profile: 'プロフィール',
    admin: '管理者',
    logout: 'ログアウト',
    
    // Common
    search: '検索',
    filter: 'フィルター',
    add: '追加',
    edit: '編集',
    delete: '削除',
    save: '保存',
    cancel: 'キャンセル',
    loading: '読み込み中',
    error: 'エラー',
    success: '成功',
    next: '次へ',
    previous: '前へ',
    
    // Admin
    adminLogin: '管理者ログイン',
    username: 'ユーザー名',
    password: 'パスワード',
    login: 'ログイン',
    amazonProductsManagement: 'Amazon製品管理',
    recipeManagement: 'レシピ管理',
    addManageProducts: '製品の追加と管理',
    addManageRecipes: 'レシピの追加と管理',
    newProduct: '新しい製品',
    newRecipe: '新しいレシピ',
    
    // Recipes
    searchRecipes: 'レシピを検索...',
    allMeals: 'すべての食事',
    breakfast: '朝食',
    lunch: '昼食',
    dinner: '夕食',
    snack: 'スナック',
    smoothie: 'スムージー',
    recipesFound: '見つかったレシピ',
    noRecipesFound: 'レシピが見つかりません',
    loadingRecipes: 'レシピを読み込み中...',
    
    // Food Scanner
    foodScannerTitle: '食品スキャナー',
    analyzeCarbs: '炭水化物を即座に分析',
    scanBarcode: 'バーコードをスキャン',
    searchFood: '食品を検索',
    carbsAnalysis: '炭水化物分析',
    totalCarbs: '総炭水化物',
    sugars: '糖',
    calories: 'カロリー',
    protein: 'タンパク質',
    fats: '脂肪',
    fiber: '繊維',
    ingredients: '成分',
    productNotFound: '製品が見つかりません',
    
    // Messages
    productAddedSuccess: '製品を追加しました',
    productDeletedSuccess: '製品を削除しました',
    recipeAddedSuccess: 'レシピを追加しました',
    recipeDeletedSuccess: 'レシピを削除しました',
    errorLoading: '読み込みエラー',
    errorSaving: '保存エラー',
    
    // Misc
    openScanner: 'スキャナーを開く',
    viewDetails: '詳細を表示',
    carbs: '炭水化物',
  },
  
  pt: {
    // Navigation
    dashboard: 'Painel',
    bloodSugar: 'Glicemia',
    meals: 'Refeições',
    medications: 'Medicamentos',
    exercise: 'Exercício',
    healthTracking: 'Rastreamento de Saúde',
    analytics: 'Análises',
    aiInsights: 'Insights de IA',
    smartFeatures: 'Recursos Inteligentes',
    foodScanner: 'Scanner de Alimentos',
    recipes: 'Receitas',
    shop: 'Loja',
    community: 'Comunidade',
    careTeam: 'Equipe de Cuidados',
    reports: 'Relatórios',
    pricing: 'Preços',
    profile: 'Perfil',
    admin: 'Admin',
    logout: 'Sair',
    
    // Common
    search: 'Pesquisar',
    filter: 'Filtrar',
    add: 'Adicionar',
    edit: 'Editar',
    delete: 'Excluir',
    save: 'Salvar',
    cancel: 'Cancelar',
    loading: 'Carregando',
    error: 'Erro',
    success: 'Sucesso',
    next: 'Próximo',
    previous: 'Anterior',
    
    // Admin
    adminLogin: 'Login Admin',
    username: 'Usuário',
    password: 'Senha',
    login: 'Entrar',
    amazonProductsManagement: 'Gerenciamento de Produtos Amazon',
    recipeManagement: 'Gerenciamento de Receitas',
    addManageProducts: 'Adicionar e gerenciar produtos',
    addManageRecipes: 'Adicionar e gerenciar receitas',
    newProduct: 'Novo Produto',
    newRecipe: 'Nova Receita',
    
    // Recipes
    searchRecipes: 'Pesquisar receitas...',
    allMeals: 'Todas as refeições',
    breakfast: 'Café da Manhã',
    lunch: 'Almoço',
    dinner: 'Jantar',
    snack: 'Lanche',
    smoothie: 'Smoothie',
    recipesFound: 'receitas encontradas',
    noRecipesFound: 'Nenhuma receita encontrada',
    loadingRecipes: 'Carregando receitas...',
    
    // Food Scanner
    foodScannerTitle: 'Scanner de Alimentos',
    analyzeCarbs: 'Analisar carboidratos instantaneamente',
    scanBarcode: 'Escanear código de barras',
    searchFood: 'Pesquisar alimento',
    carbsAnalysis: 'Análise de Carboidratos',
    totalCarbs: 'Carboidratos Totais',
    sugars: 'Açúcares',
    calories: 'Calorias',
    protein: 'Proteína',
    fats: 'Gorduras',
    fiber: 'Fibra',
    ingredients: 'Ingredientes',
    productNotFound: 'Produto não encontrado',
    
    // Messages
    productAddedSuccess: 'Produto adicionado com sucesso',
    productDeletedSuccess: 'Produto excluído com sucesso',
    recipeAddedSuccess: 'Receita adicionada com sucesso',
    recipeDeletedSuccess: 'Receita excluída com sucesso',
    errorLoading: 'Erro ao carregar',
    errorSaving: 'Erro ao salvar',
    
    // Misc
    openScanner: 'Abrir Scanner',
    viewDetails: 'Ver detalhes',
    carbs: 'carboidratos',
  },
  
  it: {
    // Navigation
    dashboard: 'Cruscotto',
    bloodSugar: 'Glicemia',
    meals: 'Pasti',
    medications: 'Farmaci',
    exercise: 'Esercizio',
    healthTracking: 'Monitoraggio Salute',
    analytics: 'Analisi',
    aiInsights: 'Approfondimenti IA',
    smartFeatures: 'Funzioni Intelligenti',
    foodScanner: 'Scanner Alimentare',
    recipes: 'Ricette',
    shop: 'Negozio',
    community: 'Comunità',
    careTeam: 'Team di Cura',
    reports: 'Rapporti',
    pricing: 'Prezzi',
    profile: 'Profilo',
    admin: 'Admin',
    logout: 'Disconnetti',
    
    // Common
    search: 'Cerca',
    filter: 'Filtra',
    add: 'Aggiungi',
    edit: 'Modifica',
    delete: 'Elimina',
    save: 'Salva',
    cancel: 'Annulla',
    loading: 'Caricamento',
    error: 'Errore',
    success: 'Successo',
    next: 'Avanti',
    previous: 'Indietro',
    
    // Admin
    adminLogin: 'Login Admin',
    username: 'Nome utente',
    password: 'Password',
    login: 'Accedi',
    amazonProductsManagement: 'Gestione Prodotti Amazon',
    recipeManagement: 'Gestione Ricette',
    addManageProducts: 'Aggiungi e gestisci prodotti',
    addManageRecipes: 'Aggiungi e gestisci ricette',
    newProduct: 'Nuovo Prodotto',
    newRecipe: 'Nuova Ricetta',
    
    // Recipes
    searchRecipes: 'Cerca ricette...',
    allMeals: 'Tutti i pasti',
    breakfast: 'Colazione',
    lunch: 'Pranzo',
    dinner: 'Cena',
    snack: 'Spuntino',
    smoothie: 'Frullato',
    recipesFound: 'ricette trovate',
    noRecipesFound: 'Nessuna ricetta trovata',
    loadingRecipes: 'Caricamento ricette...',
    
    // Food Scanner
    foodScannerTitle: 'Scanner Alimentare',
    analyzeCarbs: 'Analizza carboidrati istantaneamente',
    scanBarcode: 'Scansiona codice a barre',
    searchFood: 'Cerca cibo',
    carbsAnalysis: 'Analisi Carboidrati',
    totalCarbs: 'Carboidrati Totali',
    sugars: 'Zuccheri',
    calories: 'Calorie',
    protein: 'Proteine',
    fats: 'Grassi',
    fiber: 'Fibre',
    ingredients: 'Ingredienti',
    productNotFound: 'Prodotto non trovato',
    
    // Messages
    productAddedSuccess: 'Prodotto aggiunto con successo',
    productDeletedSuccess: 'Prodotto eliminato con successo',
    recipeAddedSuccess: 'Ricetta aggiunta con successo',
    recipeDeletedSuccess: 'Ricetta eliminata con successo',
    errorLoading: 'Errore nel caricamento',
    errorSaving: 'Errore nel salvataggio',
    
    // Misc
    openScanner: 'Apri Scanner',
    viewDetails: 'Visualizza dettagli',
    carbs: 'carboidrati',
  },
  
  ru: {
    // Navigation
    dashboard: 'Панель управления',
    bloodSugar: 'Уровень сахара в крови',
    meals: 'Питание',
    medications: 'Лекарства',
    exercise: 'Упражнения',
    healthTracking: 'Отслеживание здоровья',
    analytics: 'Аналитика',
    aiInsights: 'ИИ-анализ',
    smartFeatures: 'Умные функции',
    foodScanner: 'Сканер еды',
    recipes: 'Рецепты',
    shop: 'Магазин',
    community: 'Сообщество',
    careTeam: 'Команда по уходу',
    reports: 'Отчеты',
    pricing: 'Цены',
    profile: 'Профиль',
    admin: 'Админ',
    logout: 'Выйти',
    
    // Common
    search: 'Поиск',
    filter: 'Фильтр',
    add: 'Добавить',
    edit: 'Изменить',
    delete: 'Удалить',
    save: 'Сохранить',
    cancel: 'Отмена',
    loading: 'Загрузка',
    error: 'Ошибка',
    success: 'Успешно',
    next: 'Далее',
    previous: 'Назад',
    
    // Admin
    adminLogin: 'Вход администратора',
    username: 'Имя пользователя',
    password: 'Пароль',
    login: 'Войти',
    amazonProductsManagement: 'Управление продуктами Amazon',
    recipeManagement: 'Управление рецептами',
    addManageProducts: 'Добавить и управлять продуктами',
    addManageRecipes: 'Добавить и управлять рецептами',
    newProduct: 'Новый продукт',
    newRecipe: 'Новый рецепт',
    
    // Recipes
    searchRecipes: 'Искать рецепты...',
    allMeals: 'Все приемы пищи',
    breakfast: 'Завтрак',
    lunch: 'Обед',
    dinner: 'Ужин',
    snack: 'Перекус',
    smoothie: 'Смузи',
    recipesFound: 'найдено рецептов',
    noRecipesFound: 'Рецепты не найдены',
    loadingRecipes: 'Загрузка рецептов...',
    
    // Food Scanner
    foodScannerTitle: 'Сканер еды',
    analyzeCarbs: 'Мгновенный анализ углеводов',
    scanBarcode: 'Сканировать штрих-код',
    searchFood: 'Искать еду',
    carbsAnalysis: 'Анализ углеводов',
    totalCarbs: 'Всего углеводов',
    sugars: 'Сахара',
    calories: 'Калории',
    protein: 'Белок',
    fats: 'Жиры',
    fiber: 'Клетчатка',
    ingredients: 'Ингредиенты',
    productNotFound: 'Продукт не найден',
    
    // Messages
    productAddedSuccess: 'Продукт успешно добавлен',
    productDeletedSuccess: 'Продукт успешно удален',
    recipeAddedSuccess: 'Рецепт успешно добавлен',
    recipeDeletedSuccess: 'Рецепт успешно удален',
    errorLoading: 'Ошибка загрузки',
    errorSaving: 'Ошибка сохранения',
    
    // Misc
    openScanner: 'Открыть сканер',
    viewDetails: 'Посмотреть детали',
    carbs: 'углеводы',
  },
  
  ar: {
    // Navigation
    dashboard: 'لوحة القيادة',
    bloodSugar: 'سكر الدم',
    meals: 'الوجبات',
    medications: 'الأدوية',
    exercise: 'التمارين',
    healthTracking: 'تتبع الصحة',
    analytics: 'التحليلات',
    aiInsights: 'رؤى الذكاء الاصطناعي',
    smartFeatures: 'الميزات الذكية',
    foodScanner: 'ماسح الطعام',
    recipes: 'الوصفات',
    shop: 'المتجر',
    community: 'المجتمع',
    careTeam: 'فريق الرعاية',
    reports: 'التقارير',
    pricing: 'التسعير',
    profile: 'الملف الشخصي',
    admin: 'المشرف',
    logout: 'تسجيل الخروج',
    
    // Common
    search: 'بحث',
    filter: 'تصفية',
    add: 'إضافة',
    edit: 'تعديل',
    delete: 'حذف',
    save: 'حفظ',
    cancel: 'إلغاء',
    loading: 'جاري التحميل',
    error: 'خطأ',
    success: 'نجاح',
    next: 'التالي',
    previous: 'السابق',
    
    // Admin
    adminLogin: 'تسجيل دخول المشرف',
    username: 'اسم المستخدم',
    password: 'كلمة المرور',
    login: 'تسجيل الدخول',
    amazonProductsManagement: 'إدارة منتجات أمازون',
    recipeManagement: 'إدارة الوصفات',
    addManageProducts: 'إضافة وإدارة المنتجات',
    addManageRecipes: 'إضافة وإدارة الوصفات',
    newProduct: 'منتج جديد',
    newRecipe: 'وصفة جديدة',
    
    // Recipes
    searchRecipes: 'البحث عن الوصفات...',
    allMeals: 'جميع الوجبات',
    breakfast: 'الإفطار',
    lunch: 'الغداء',
    dinner: 'العشاء',
    snack: 'وجبة خفيفة',
    smoothie: 'عصير',
    recipesFound: 'الوصفات الموجودة',
    noRecipesFound: 'لم يتم العثور على وصفات',
    loadingRecipes: 'تحميل الوصفات...',
    
    // Food Scanner
    foodScannerTitle: 'ماسح الطعام',
    analyzeCarbs: 'تحليل الكربوهيدرات فوراً',
    scanBarcode: 'مسح الباركود',
    searchFood: 'البحث عن طعام',
    carbsAnalysis: 'تحليل الكربوهيدرات',
    totalCarbs: 'إجمالي الكربوهيدرات',
    sugars: 'السكريات',
    calories: 'السعرات الحرارية',
    protein: 'البروتين',
    fats: 'الدهون',
    fiber: 'الألياف',
    ingredients: 'المكونات',
    productNotFound: 'المنتج غير موجود',
    
    // Messages
    productAddedSuccess: 'تمت إضافة المنتج بنجاح',
    productDeletedSuccess: 'تم حذف المنتج بنجاح',
    recipeAddedSuccess: 'تمت إضافة الوصفة بنجاح',
    recipeDeletedSuccess: 'تم حذف الوصفة بنجاح',
    errorLoading: 'خطأ في التحميل',
    errorSaving: 'خطأ في الحفظ',
    
    // Misc
    openScanner: 'فتح الماسح',
    viewDetails: 'عرض التفاصيل',
    carbs: 'الكربوهيدرات',
  }
};
